import { useState, useEffect } from 'react';
import Head from 'next/head';
import Router from 'next/router';
import Dynamic from 'next/dynamic';
import { storeWrapper } from 'store';
import { useSelector } from 'react-redux';
import { useUser } from '@auth0/nextjs-auth0/client';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SkeletonFull from 'components/SchoolsComponents/SchoolGalleryComponent/SkeletonFull';
import Summary from 'components/SummaryComponent';
import BreadcrumbGeneratorComponent from 'components/BreadcrumbGeneratorComponent';
import Header from 'components/Shared/Header';

import {
  LabelWithDescriptionComponent,
  SchoolGalleryComponent,
  SchoolLocationComponent,
  SchoolTestimoniesComponent,
  VideoComponent,
} from 'components/SchoolsComponents';

import { fetchPackage } from 'services/packageService';
import {
  fetchSchoolDataFilter,
  getMainSchoolImage,
} from 'services/schoolService';
import { fetchCountries } from 'services/countryService';
import { createOrder } from 'services/abroadService';

import { DefaultMenuNav } from 'mocks/menuNav';

import { buildAddress } from 'utils/locationUtils';
import { loadState } from 'utils/localStorage';
import { categoriesSlugInfo, categorySlug } from 'utils/schoolUtils';
import {
  hasPermissionByRole,
  getSalesExecutiveCode,
} from 'utils/auth/authHelper';
import {
  formatDate,
  getDaysAdded,
  getNextBusinessDayInRange,
} from 'utils/date';

const Footer = Dynamic(() => import('components/Footer'), { ssr: true });

function SchoolPackagePage({ packet, isSalesExecutive }) {
  const { user } = useUser();
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const defaultErrorMessage =
    'Ocorreu uma instabilidade no sistema, por favor tente mais tarde';
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);
  const [mainImage, setMainImage] = useState('');
  const [openGallery, setOpenGallery] = useState(false);
  const title = `${packet?.title} - ${packet?.id} - Fluencypass`;

  const handleOpenGallery = () => setOpenGallery(!openGallery);
  const handleShowLoader = (show) => setShowLoader(show);

  const handleCloseErrorMessage = (event, reason) => {
    if (reason === 'clickaway') return;

    setErrorMessage(defaultErrorMessage);

    setShowErrorMessage(false);
  };

  let { startDate } = useSelector((store) => store.packageReducer.detail);
  const schoolDetail = useSelector((store) => store.schoolDetailReducer);

  if (startDate === undefined) {
    startDate = new Date(
      formatDate(
        getNextBusinessDayInRange(
          getDaysAdded(
            new Date(),
            parseInt(process.env.DAYS_BEFORE_BOOKING, 10)
          )
        ),
        'inverse-dashed'
      )
    );
  }

  const order = async () => {
    handleShowLoader(true);

    try {
      const localState = loadState();
      const { email } = localState?.lead ?? '';

      const requestBody = {
        accommodations: [],
        additionals: [],
        insurances: [],
        courses: [],
        schoolId: packet?.schoolId,
        package: {
          id: parseInt(packet?.id, 10),
          weekQuantity: 2,
          startDate,
        },
        email,
        checkin: false,
        checkout: false,
        step: 0,
        salesExecutiveCode: getSalesExecutiveCode(user),
        deposit: parseFloat(schoolDetail.deposit, 10),
      };

      const result = await createOrder(requestBody);

      Router.push(`/book/${result.hash}`);
    } catch (error) {
      setErrorMessage(error.message);
      setShowErrorMessage(true);
      handleShowLoader(false);
    }
  };

  useEffect(() => {
    async function getMainImage() {
      const response = await getMainSchoolImage(packet?.schoolId);

      setMainImage(response.url);
    }

    getMainImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box bgcolor="background.paper">
      <Head>
        <title>{title}</title>
        <meta
          content="Fluencypass - Seu intercâmbio sem taxa de agência"
          key="title"
          property="og:title"
        />

        <meta
          content="O jeito mais fácil para fazer seu intercâmbio. Seu intercâmbio com o preço direto da escola sem as taxas das agências!"
          key="description"
          property="og:description"
        />

        <meta
          name="description"
          content={`Pacote promocional: ${packet?.title}`}
        />
        <meta
          name="og:description"
          content={`Pacote promocional: ${packet?.title}`}
        />
      </Head>

      <Header menuNavHeader={DefaultMenuNav} menuNavSideMenu={DefaultMenuNav} />

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={5000}
        onClose={handleCloseErrorMessage}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={handleCloseErrorMessage}
        >
          {errorMessage}
        </MuiAlert>
      </Snackbar>

      <Backdrop sx={{ zIndex: 1201, color: '#fff' }} open={showLoader}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container maxWidth="lg" sx={{ my: 2 }}>
        <Box mt={3}>
          <BreadcrumbGeneratorComponent
            mb={0}
            paths={[
              { type: 'link', text: 'Pacotes', href: '/packages' },
              {
                text: packet?.schoolName,
              },
            ]}
          />
        </Box>

        <Box mb={2} mt={2} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h2" component="h1">
            <Box fontSize={32} fontWeight={900} textAlign="center">
              {packet?.schoolName}
            </Box>
          </Typography>
          <Box component="span" ml={1}>
            <LabelWithDescriptionComponent
              categories={categorySlug[packet?.school?.category]}
              categoriesSlugInfo={categoriesSlugInfo[packet?.school?.category]}
            />
          </Box>
        </Box>

        <Box mb={4}>
          {!mainImage ? (
            <SkeletonFull />
          ) : (
            <Box
              sx={{
                backgroundImage: `url(${mainImage})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                cursor: 'pointer',
                height: { xs: '256px', sm: '350px' },
                width: '100%',
                position: 'relative',
              }}
              onClick={handleOpenGallery}
            >
              <Button
                sx={{
                  fontWeight: 600,
                  position: 'absolute',
                  right: 24,
                  textTransform: 'capitalize',
                  top: 24,
                  backgroundColor: '#f6f7f7',
                  color: '#2A1D1D',

                  '&:hover': {
                    backgroundColor: '#f6f7f7',
                  },
                }}
                variant="contained"
                onClick={handleOpenGallery}
              >
                Ver fotos
              </Button>
            </Box>
          )}
        </Box>

        {openGallery && (
          <SchoolGalleryComponent
            id={packet?.schoolId}
            schoolName={packet?.schoolName}
            open={openGallery}
            handleClose={handleOpenGallery}
          />
        )}

        <Typography variant="h4" component="h1">
          <Box mb={2} mt={4} fontWeight={900} textAlign="center">
            {packet?.title}
          </Box>
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            {isSalesExecutive && packet?.privateInformation && (
              <>
                <Grid item xs={8} sm={4}>
                  <Box
                    textAlign="center"
                    bgcolor="background.primary.dark"
                    color="primary.contrastText"
                    py={0.5}
                    borderRadius="4px"
                  >
                    <Typography
                      textTransform="uppercase"
                      variant="subtitle2"
                      component="div"
                    >
                      <Box fontWeight={900}>Informações Extras</Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={2} mb={4} ml={2}>
                    {ReactHtmlParser(packet?.privateInformation)}
                  </Box>
                </Grid>
              </>
            )}
            <Grid item xs={8} sm={4}>
              <Box
                textAlign="center"
                bgcolor="background.primary.dark"
                color="primary.contrastText"
                py={0.5}
                borderRadius="4px"
              >
                <Typography
                  textTransform="uppercase"
                  variant="subtitle2"
                  component="div"
                >
                  <Box fontWeight={900}>Sobre o Curso</Box>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box mt={2} mb={4} ml={2}>
                <Box mb={1}>
                  {ReactHtmlParser(packet?.courseWeeks)} semanas com{' '}
                  {ReactHtmlParser(packet?.classesQuantity)} por semana no
                  período da {ReactHtmlParser(packet?.period)} (
                  {ReactHtmlParser(packet?.schedule)})
                </Box>
                <Box>{ReactHtmlParser(packet?.courseType)}</Box>
              </Box>
            </Grid>
            {packet?.accommodationType !== '' && (
              <>
                <Grid item xs={8} sm={4}>
                  <Box
                    textAlign="center"
                    bgcolor="background.primary.dark"
                    color="primary.contrastText"
                    py={0.5}
                    borderRadius="4px"
                  >
                    <Typography
                      textTransform="uppercase"
                      variant="subtitle2"
                      component="div"
                    >
                      <Box fontWeight={900}>Acomodação</Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={2} mb={4} ml={2}>
                    <Box mb={1}>
                      {ReactHtmlParser(packet?.accommodationName)}
                      {packet?.accommodationWeeks &&
                        ReactHtmlParser(packet?.accommodationWeeks)}
                    </Box>
                    <Box>
                      {ReactHtmlParser(packet?.accommodationType)} -{' '}
                      {ReactHtmlParser(packet?.bedroomType)} -{' '}
                      {ReactHtmlParser(packet?.meal)}
                    </Box>
                    {packet?.accommodationDescription !== 'NULL' && (
                      <Box>
                        {ReactHtmlParser(packet?.accommodationDescription)}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </>
            )}
            {packet?.included.length > 0 && (
              <>
                <Grid item xs={8} sm={4}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      bgcolor: 'background.primary.dark',
                      color: 'primary.contrastText',
                      py: 0.5,
                    }}
                    borderRadius="4px"
                  >
                    <Typography
                      textTransform="uppercase"
                      variant="subtitle2"
                      component="div"
                    >
                      <Box fontWeight={900}>Incluso no pacote</Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={2} mb={4} ml={2}>
                    {ReactHtmlParser(packet?.included)}
                  </Box>
                </Grid>
              </>
            )}
            {packet?.notIncluded.length > 0 && (
              <>
                <Grid item xs={8} sm={4}>
                  <Box
                    textAlign="center"
                    bgcolor="background.primary.dark"
                    color="primary.contrastText"
                    py={0.5}
                    borderRadius="4px"
                  >
                    <Typography
                      textTransform="uppercase"
                      variant="subtitle2"
                      component="div"
                    >
                      <Box fontWeight={900}>Não incluso no pacote</Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={2} mb={4} ml={2}>
                    {ReactHtmlParser(packet?.notIncluded)}
                  </Box>
                </Grid>
              </>
            )}
            {packet?.rules.length > 0 && (
              <>
                <Grid item xs={8} sm={4}>
                  <Box
                    textAlign="center"
                    bgcolor="background.primary.dark"
                    color="primary.contrastText"
                    py={0.5}
                    borderRadius="4px"
                  >
                    <Typography
                      textTransform="uppercase"
                      variant="subtitle2"
                      component="div"
                    >
                      <Box fontWeight={900}>Regras</Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={2} mb={4} ml={2}>
                    {ReactHtmlParser(packet?.rules)}
                  </Box>
                </Grid>
              </>
            )}
            {packet?.additionalInformation.length > 0 && (
              <>
                <Grid item xs={8} sm={4}>
                  <Box
                    textAlign="center"
                    bgcolor="background.primary.dark"
                    color="primary.contrastText"
                    py={0.5}
                    borderRadius="4px"
                  >
                    <Typography
                      textTransform="uppercase"
                      variant="subtitle2"
                      component="div"
                    >
                      <Box fontWeight={900}>Informações adicionais</Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={2} mb={4} ml={2}>
                    {ReactHtmlParser(packet?.additionalInformation)}
                  </Box>
                </Grid>
              </>
            )}
            <Grid item xs={8} sm={4}>
              <Box
                textAlign="center"
                bgcolor="background.primary.dark"
                color="primary.contrastText"
                py={0.5}
                borderRadius="4px"
              >
                <Typography
                  textTransform="uppercase"
                  variant="subtitle2"
                  component="div"
                >
                  <Box fontWeight={900}>Sobre a escola</Box>
                </Typography>
              </Box>
            </Grid>

            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12}>
                <Box pb={1}>
                  <Typography variant="body2" component="div">
                    {ReactHtmlParser(packet?.schoolDescription)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {packet?.imagePath && (
              <VideoComponent imagePath={packet?.imagePath} />
            )}

            {packet?.location && (
              <SchoolLocationComponent
                address={buildAddress(packet?.location)}
              />
            )}
          </Grid>

          <Grid item sm={4} xs={12}>
            {packet?.expired ? (
              <Box
                display="flex"
                alignItems="center"
                bgcolor="background.secondary.main"
                padding={3}
                borderRadius="10px"
              >
                <CheckCircleIcon />
                &nbsp;
                <Typography variant="subtitle1">Promoção esgotada</Typography>
              </Box>
            ) : (
              <Summary
                createOrder={order}
                dateParams={{}}
                isSalesExecutive={isSalesExecutive}
              />
            )}
          </Grid>
        </Grid>
      </Container>

      {packet?.comments?.length > 0 && (
        <SchoolTestimoniesComponent
          comments={packet?.comments}
          rating={packet?.rating}
        />
      )}

      <Footer maxWidth="lg" showCountries />
    </Box>
  );
}

export const getServerSideProps = storeWrapper.getServerSideProps(
  async ({ req, res, store, query }) => {
    const isSalesExecutive = await hasPermissionByRole(req, res, 'expert');

    await fetchCountries(store.dispatch);
    await fetchSchoolDataFilter(store.dispatch);

    const packet = await fetchPackage(query.id, store.dispatch);

    if (!packet?.id) {
      return {
        notFound: true,
      };
    }

    return {
      props: { packet, isSalesExecutive },
    };
  }
);

SchoolPackagePage.propTypes = {
  packet: PropTypes.instanceOf(Object),
  isSalesExecutive: PropTypes.bool,
};

SchoolPackagePage.defaultProps = {
  packet: {},
  isSalesExecutive: false,
};

export default SchoolPackagePage;
